import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search, MapPin, Store, ChevronRight, Clock, Zap, ClipboardX, DollarSign,
  ShieldCheck, Users, Building2, HeartHandshake, Landmark, ShoppingBasket,
  Leaf, Recycle, Mail, Linkedin, ArrowRight, TrendingDown, Sprout
} from 'lucide-react';
import Logo from '../components/Logo';
import BuildInfo from '../components/BuildInfo';
import Footer from '../components/legal/Footer';
import SiteDisclaimer from '../components/legal/SiteDisclaimer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PARTNERSHIPS_EMAIL = 'partnerships@dealshaq.com';
const FOUNDER_EMAIL = 'alfred@dealshaq.com';
const CONTACT_EMAIL = 'info@dealshaq.com';

// Rounded pill CTA — orange (primary action) or teal (secondary action)
function CtaButton({ href, to, color = 'orange', children, testId, external }) {
  const palette = color === 'teal'
    ? 'bg-teal-500 hover:bg-teal-600'
    : color === 'purple'
    ? 'bg-purple-600 hover:bg-purple-700'
    : 'bg-orange-500 hover:bg-orange-600';
  const cls = `inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-lg transition-colors ${palette} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/70`;
  if (to) {
    return <Link to={to} className={cls} data-testid={testId}>{children}</Link>;
  }
  return (
    <a
      href={href}
      className={cls}
      data-testid={testId}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

// Small link styled as a text CTA with arrow
function TextCta({ href, to, children, testId, external }) {
  const cls = 'inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700 hover:text-purple-900 transition-colors';
  if (to) return <Link to={to} className={cls} data-testid={testId}>{children} <ArrowRight className="w-4 h-4" /></Link>;
  return (
    <a href={href} className={cls} data-testid={testId} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {children} <ArrowRight className="w-4 h-4" />
    </a>
  );
}

export default function GrocersDirectory() {
  const [retailers, setRetailers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRetailers();
  }, []);

  const fetchRetailers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/directory/retailers`);
      if (res.ok) {
        const data = await res.json();
        setRetailers(data.retailers || []);
      }
    } catch (e) {
      console.error('Failed to fetch retailers:', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = retailers.filter(r =>
    !searchQuery ||
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.city || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.address || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="grocers-directory">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="small" />
            <div>
              <h1 className="text-lg font-bold text-gray-900">DealShaq</h1>
              <p className="text-xs text-gray-500">Surplus Food, Real-Time Access</p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <a href="#grocer-directory" className="hidden sm:inline text-sm text-gray-600 hover:text-purple-700 font-medium" data-testid="nav-browse-link">Browse Grocers</a>
            <Link to="/retailer" className="hidden sm:inline text-sm text-gray-600 hover:text-purple-700 font-medium" data-testid="nav-for-grocers-link">For Grocers</Link>
            <Link to="/consumer" className="text-sm text-purple-600 hover:text-purple-700 font-medium" data-testid="consumer-login-link">Consumer Login</Link>
            <Link to="/admin" className="text-xs text-gray-400 hover:text-gray-600" data-testid="nav-admin-link">Admin</Link>
          </div>
        </div>
      </header>

      {/* 1. Hero */}
      <section className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-20 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 tracking-tight" data-testid="hero-headline">
            DealShaq — Surplus Food, Real-Time Access
          </h2>
          <p className="text-purple-100 text-base md:text-lg mb-5 max-w-2xl mx-auto">
            Helping grocers sell unsold surplus food in real time, strengthening community resilience and reducing waste.
          </p>
          <div className="bg-white/10 border border-amber-300/40 rounded-xl px-5 py-4 max-w-2xl mx-auto mb-8 backdrop-blur-sm" data-testid="hero-urgency">
            <p className="text-amber-200 text-sm sm:text-base font-medium">
              Beginning <strong>October 1</strong>, states face a <strong>50% increase</strong> in SNAP administrative costs. DealShaq helps communities absorb the impact by turning surplus food into affordable access.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <CtaButton href={`mailto:${PARTNERSHIPS_EMAIL}?subject=Start%20a%201-Month%20DealShaq%20Pilot`} color="orange" testId="hero-cta-pilot" external>
              Start a 1-Month Pilot
            </CtaButton>
            <CtaButton to="/retailer" color="teal" testId="hero-cta-grocer">
              Join as a Participating Grocer
            </CtaButton>
          </div>
        </div>
      </section>

      {/* County pilot banner (optional enhancement) */}
      <div className="bg-purple-900 text-purple-100" data-testid="county-banner">
        <div className="max-w-6xl mx-auto px-4 py-2.5 text-center text-xs sm:text-sm font-medium tracking-wide">
          <MapPin className="w-4 h-4 inline mr-1.5 -mt-0.5 text-amber-300" />
          Now piloting in <strong className="text-white">Fairfax</strong> · <strong className="text-white">Loudoun</strong> · <strong className="text-white">Prince William</strong> counties
        </div>
      </div>

      {/* 2. Mission — Why DealShaq Matters Now */}
      <section id="mission" className="bg-white scroll-mt-20" data-testid="section-mission">
        <div className="max-w-5xl mx-auto px-4 py-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 text-center">Why DealShaq Matters Now</h2>
          <p className="text-gray-600 text-base leading-relaxed max-w-3xl mx-auto text-center mb-8">
            Beginning October 1, states face a 50% increase in their share of SNAP administrative costs. This shift will slow processing, increase churn, and reduce purchasing power for thousands of households. Food banks across Virginia are bracing for increased demand. DealShaq provides a parallel access channel that doesn&rsquo;t rely on government systems — making surplus grocery food available at low cost, instantly, and without administrative friction.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: TrendingDown, label: '50% higher SNAP admin costs', tone: 'text-rose-600 bg-rose-50' },
              { icon: Clock, label: 'Effective October 1', tone: 'text-amber-600 bg-amber-50' },
              { icon: ShieldCheck, label: 'No government systems needed', tone: 'text-emerald-600 bg-emerald-50' },
              { icon: Zap, label: 'Instant, low-cost access', tone: 'text-purple-600 bg-purple-50' },
            ].map((it) => (
              <div key={it.label} className="rounded-xl border border-gray-100 p-4 text-center">
                <div className={`w-11 h-11 mx-auto mb-2 rounded-lg flex items-center justify-center ${it.tone}`}>
                  <it.icon className="w-6 h-6" />
                </div>
                <p className="text-xs sm:text-sm font-medium text-gray-700">{it.label}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <TextCta to="/press" testId="mission-cta">Learn More About the October Transition</TextCta>
          </div>
        </div>
      </section>

      {/* 3. How It Works */}
      <section id="how-it-works" className="bg-gray-50 scroll-mt-20" data-testid="section-how-it-works">
        <div className="max-w-5xl mx-auto px-4 py-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">How DealShaq Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { icon: Clock, title: 'Real-Time Surplus Listings', body: 'Grocers post surplus items as they approach end-of-day or end-of-shelf life.' },
              { icon: ShoppingBasket, title: 'Instant Community Access', body: 'Households browse nearby surplus and purchase at reduced prices.' },
              { icon: ClipboardX, title: 'Zero Administrative Burden', body: 'No eligibility checks, no paperwork, no changes to POS systems.' },
              { icon: DollarSign, title: 'Revenue Recovery for Grocers', body: 'Stores earn from inventory that would otherwise be discarded.' },
              { icon: HeartHandshake, title: 'Local Resilience for Counties', body: 'Supports Fairfax, Loudoun, and Prince William households facing benefit interruptions.' },
            ].map((it) => (
              <div key={it.title} className="flex items-start gap-4 bg-white rounded-xl border border-gray-100 p-5">
                <div className="w-11 h-11 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
                  <it.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">{it.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{it.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <TextCta href="#who-we-serve" testId="how-cta">Explore the Model</TextCta>
          </div>
        </div>
      </section>

      {/* 4. Who We Serve */}
      <section id="who-we-serve" className="bg-white scroll-mt-20" data-testid="section-who-we-serve">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">Who We Serve</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Store, title: 'Grocers & Retailers', body: 'Reduce shrink, recover value, strengthen ESG commitments.', tone: 'text-purple-600 bg-purple-50' },
              { icon: HeartHandshake, title: 'Food Charities & Nonprofits', body: 'Lower pressure on food banks by offering an alternative access channel.', tone: 'text-rose-600 bg-rose-50' },
              { icon: Landmark, title: 'Municipal Leaders', body: 'Provide a community-level resilience tool during SNAP disruptions.', tone: 'text-blue-600 bg-blue-50' },
              { icon: Users, title: 'Households', body: 'Affordable access to fresh food, right when they need it most.', tone: 'text-emerald-600 bg-emerald-50' },
            ].map((it) => (
              <div key={it.title} className="rounded-xl border border-gray-100 p-6 text-center h-full">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${it.tone}`}>
                  <it.icon className="w-6 h-6" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{it.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <TextCta href="#impact-snapshot" testId="who-cta">See County Impact</TextCta>
          </div>
        </div>
      </section>

      {/* 5. Retailer Pilot */}
      <section id="retailer-pilot" className="bg-gradient-to-br from-purple-50 to-indigo-100 scroll-mt-20" data-testid="section-retailer-pilot">
        <div className="max-w-4xl mx-auto px-4 py-14 text-center">
          <Building2 className="w-10 h-10 mx-auto mb-4 text-purple-600" />
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Retailers Are Piloting DealShaq</h2>
          <p className="text-gray-700 text-base leading-relaxed max-w-2xl mx-auto mb-8">
            Premium and mainstream retailers alike face rising shrink, increased community expectations, and higher demand for low-cost food options. DealShaq is a low-lift, high-impact way to prepare for the October transition.
          </p>
          <CtaButton href={`mailto:${PARTNERSHIPS_EMAIL}?subject=Request%20a%201-Month%20DealShaq%20Pilot`} color="purple" testId="pilot-cta" external>
            Request a 1-Month Pilot
          </CtaButton>
        </div>
      </section>

      {/* 6. Impact Snapshot */}
      <section id="impact-snapshot" className="bg-white scroll-mt-20" data-testid="section-impact-snapshot">
        <div className="max-w-5xl mx-auto px-4 py-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">Impact Snapshot</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Recycle, label: 'Surplus diverted from waste' },
              { icon: DollarSign, label: 'Revenue recovered for grocers' },
              { icon: ShoppingBasket, label: 'Affordable access for households' },
              { icon: HeartHandshake, label: 'Reduced pressure on food banks' },
              { icon: Landmark, label: 'Stronger county-level resilience' },
              { icon: Leaf, label: 'Lower carbon footprint from rescued food' },
            ].map((it) => (
              <div key={it.label} className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                <div className="w-10 h-10 rounded-lg bg-white text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <it.icon className="w-5 h-5" />
                </div>
                <p className="text-sm font-medium text-gray-800">{it.label}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <TextCta href="#founder" testId="impact-cta">About DealShaq</TextCta>
          </div>
        </div>
      </section>

      {/* 7. Founder */}
      <section id="founder" className="bg-gray-50 scroll-mt-20" data-testid="section-founder">
        <div className="max-w-3xl mx-auto px-4 py-14 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">Meet the Founder</h2>
          <div className="flex flex-col items-center">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-4" data-testid="founder-avatar" aria-label="Alfred Toussaint">
              AT
            </div>
            <h3 className="text-xl font-bold text-gray-900">Alfred Toussaint</h3>
            <p className="text-sm text-purple-700 font-medium mb-3">Founder &amp; CEO, DealShaq</p>
            <p className="text-gray-600 text-base leading-relaxed max-w-xl mb-5">
              Committed to building community-based food-access resilience across Virginia and beyond.
            </p>
            <div className="flex items-center gap-3 mb-6">
              <a
                href="https://www.linkedin.com/company/dealshaq"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-blue-600 hover:bg-blue-50 transition-colors"
                data-testid="founder-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href={`mailto:${FOUNDER_EMAIL}`}
                className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="founder-email"
                aria-label="Email Alfred"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
            <CtaButton href={`mailto:${FOUNDER_EMAIL}?subject=Hello%20from%20DealShaq.com`} color="purple" testId="founder-cta" external>
              Contact Alfred
            </CtaButton>
          </div>
        </div>
      </section>

      {/* Grocer Directory — search + participating grocers (PRESERVED) */}
      <section id="grocer-directory" className="bg-white scroll-mt-20" data-testid="section-directory">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-2">
              <Sprout className="w-7 h-7 text-emerald-600" /> Browse Participating Grocers
            </h2>
            <p className="text-gray-600 text-base mb-6">Find surplus deals near you — save up to 75% on quality groceries.</p>
            <div className="max-w-md mx-auto relative">
              <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <Input
                data-testid="grocers-search"
                placeholder="Search by store name or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11 bg-white text-gray-800 border border-gray-200 shadow-sm"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-500">
              {filtered.length} {filtered.length === 1 ? 'grocer' : 'grocers'} found
            </p>
            <Badge variant="secondary" className="text-xs">
              Surplus-Based Discounts: 50-75% OFF
            </Badge>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading grocers...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Store className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No grocers found matching your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(filtered || []).map((retailer) => (
                <Link
                  key={retailer.id}
                  to={`/${retailer.slug}`}
                  data-testid={`retailer-card-${retailer.slug}`}
                >
                  <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                          {retailer.logo_url ? (
                            <img
                              src={retailer.logo_url}
                              alt={retailer.name}
                              className="w-10 h-10 rounded-md object-cover"
                            />
                          ) : (
                            <Store className="w-6 h-6 text-purple-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {retailer.name}
                          </h3>
                          {retailer.address && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{retailer.address}</span>
                            </p>
                          )}
                          {retailer.city && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {retailer.city}{retailer.state ? `, ${retailer.state}` : ''}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* Legal disclaimer directly under the grocer directory grid */}
          <div className="mt-8 border-t border-gray-100 pt-6">
            <SiteDisclaimer />
          </div>
        </div>
      </section>

      {/* 8. Landing footer links (About, Contact, Press, Privacy) */}
      <div className="bg-gray-900 text-gray-300" data-testid="landing-footer-links">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size="small" />
            <span className="text-sm text-gray-400">Surplus Food, Real-Time Access</span>
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <a href="#mission" className="hover:text-white transition-colors" data-testid="footer-about">About DealShaq</a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white transition-colors" data-testid="footer-contact">Contact</a>
            <Link to="/press" className="hover:text-white transition-colors" data-testid="footer-press">Press</Link>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
          </nav>
        </div>
      </div>

      {/* Footer — shared legal links (Terms, Privacy, Cookies, Refund, Support) */}
      <Footer variant="consumer" />
      <BuildInfo />
    </div>
  );
}
